import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { map, Observable, Subject, take } from 'rxjs';
import * as _ from 'lodash-es';
import { GridSaveBuiltInHandlersService } from './grid-save-built-in-handlers.service';

export type SaveHandler = (callName: string, data: any) => Observable<any> | void;

@Injectable({
  providedIn: 'root',
})
export class GridSaveFactoryService {
  GENERAL_CONFIG: any;
  private saveServiceMethods: any[] = [];
  private methodsLoaded = false;
  private saveHandlers = new Map<string, SaveHandler>();

  gridRefreshNeeded$ = new Subject<string>();
  commentsReloadNeeded$ = new Subject<string>();
  /**
   * Emits the callName whenever a registered save handler errors out (HTTP
   * error code or callSuccess=0). Screens that render their own grid outside
   * the workspace activeScreen mechanism (e.g. admin gizmoGrids) can subscribe
   * to force-reload the grid so a rejected edit is not left looking saved.
   */
  gridSaveError$ = new Subject<string>();

  constructor(
    private http: HttpClient,
    private gridSaveBuiltInHandlersService: GridSaveBuiltInHandlersService
  ) {
    this.GENERAL_CONFIG = environment;
    this.loadSaveServiceMethods();
    this.gridSaveBuiltInHandlersService.registerBuiltInHandlers(this.registerSaveHandler.bind(this));
  }

  private loadSaveServiceMethods(): void {
    this.http
      .post<any>(this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=6beggz', {})
      .pipe(take(1))
      .subscribe(
        (response: any) => {
          this.saveServiceMethods = response?.jsonObject || [];
          this.methodsLoaded = true;
        },
        () => {
          this.saveServiceMethods = [];
          this.methodsLoaded = true;
        }
      );
  }

  registerSaveHandler(callName: string, handler: SaveHandler): void {
    this.saveHandlers.set(callName, handler);
  }

  saveGridData(url: string, data: any) {
    const body = new FormData();
    _.map(data, (v: any, k: any) => {
      if (v !== null && v !== undefined) {
        body.append(k, typeof v === 'object' ? JSON.stringify(v) : v);
      }
    });
    return this.http.post(this.GENERAL_CONFIG.base_url + url, body).pipe(
      map((response: any) => {
        if (response.callSuccess == '1') {
          return response;
        }
        throw new Error('Save failed: ' + (response.errorMessage || 'Unknown error'));
      })
    );
  }

  saveSwitch(callName: string, data: any, globalParams: any): void {
    if (callName === 'rowValidation') {
      if (data.rowData?.validated) {
        data.rowData.validated = 'false';
      }
      return;
    }

    const handler = this.saveHandlers.get(callName);
    if (handler) {
      const result = handler(callName, data);
      if (result && typeof (result as any).subscribe === 'function') {
        (result as Observable<any>).pipe(take(1)).subscribe(
          (response: any) => {
            if (response?.skipped) {
              return;
            }
            this.postSaveCallback(callName, data);
            if (response?.refreshGrid) {
              this.gridRefreshNeeded$.next(callName);
            }
          },
          (err: any) => {
            console.error('[GridSaveFactory] Handler save error:', callName, err);
            // The handler/wrapSave already surfaced the message to the user.
            // Notify subscribers so self-managed grids can roll back the
            // optimistic UI change by reloading the true server state.
            this.gridSaveError$.next(callName);
          }
        );
      } else {
        this.postSaveCallback(callName, data);
      }
      return;
    }

    const callObj = _.find(this.saveServiceMethods, { SCREEN_NAME: callName });
    if (!callObj) {
      console.warn('[GridSaveFactory] No save method mapping for:', callName);
      return;
    }

    console.warn(
      '[GridSaveFactory] No Angular handler registered for:', callName,
      '(AngularJS service:', callObj.SERVICE_NAME + '.' + callObj.METHOD_NAME + ')',
      '— register via registerSaveHandler()'
    );
  }

  private postSaveCallback(callName: string, data?: any): void {
    const callObj = _.find(this.saveServiceMethods, { SCREEN_NAME: callName });
    if (callObj?.CALLBACK_METHOD == 1) {
      this.gridRefreshNeeded$.next(callName);
    }
    if (data?.colData?.audit_cells === '1') {
      this.commentsReloadNeeded$.next('all');
    }
  }

  saveAuditCellData(data: any, globalParams: any, filterCache?: any): void {
    const oldValue = data.oldRowData?.[data.colData?.map];
    const params: any = {
      cell_audit_data: data.rowData?.[data.colData?.map],
      type: 'grid',
      screen_key: globalParams?.screen_key,
      activity_key: globalParams?.activity_key,
      row_key: data.row_id,
      col_key: data.col_id,
      location_element: data.selected_type,
      oldCellData: oldValue,
    };
    if (filterCache) {
      params.filters = typeof filterCache === 'string' ? filterCache : JSON.stringify(filterCache);
    }

    this.http
      .post<any>(this.GENERAL_CONFIG.base_url + '/saveAuditCell', params)
      .pipe(take(1))
      .subscribe(
        (response: any) => {
          if (response?.callSuccess == 1) {
            this.commentsReloadNeeded$.next('all');
          }
        },
        (err: any) => console.error('[GridSaveFactory] Audit cell save error:', err)
      );
  }

  colTemplatesUpdate(
    gridKey: string, type: string, data: any,
    filterParams?: any, globalParams?: any
  ): Observable<any> {
    let params: any = {
      grid_key: gridKey,
      type: type,
      name: data.name,
      template_key: data.template_key,
      default_template: data.default_template,
      system: data.system,
      columns: JSON.stringify(data.columns || []),
      fixed_columns: data.fixed_columns,
      header_height: data.header_height,
    };
    if (filterParams) {
      params = _.merge({}, filterParams, params);
    }
    if (globalParams) {
      params = _.merge({}, globalParams, params);
    }

    const obs = this.http
      .post<any>(this.GENERAL_CONFIG.base_url + '/saveGridColumnTemplate', params)
      .pipe(
        take(1),
        map((response: any) => {
          if (response.callSuccess != '0') {
            if (type === 'create') {
              data.template_key = response.success;
            }
            return response;
          }
          throw new Error('Error occurred saving template data.');
        })
      );

    obs.subscribe(
      () => {},
      (err: any) => console.error('[GridSaveFactory] Column template save error:', err)
    );

    return obs;
  }
}
